import React, { useState } from "react";
import axios from "axios";
import BasicButton from "../basics/BasicButton";
import "./aotd.css";
import { setHeaders, url } from "../../api";
import { toast } from "react-toastify";
import emojiRegex from 'emoji-regex';
import GraphemeSplitter from "grapheme-splitter";
import { FaExclamationCircle } from "react-icons/fa";

interface NextAOTDProps {
    nextEmojis: string[];
    onEmojisSet: (emojis: string[]) => void;
}

// improved emoji validation that allows multi-code-point sequences (e.g. 👨‍👩‍👧‍👦, 🏕️)
const splitter = new GraphemeSplitter();

const isEmojiCharacter = (str: string): boolean => {
    const cleaned = str.trim();

    // 1️⃣ split the string into extended-grapheme clusters
    const clusters = typeof (Intl as any).Segmenter !== "undefined"
        ? [...new (Intl as any).Segmenter(undefined, { granularity: "grapheme" }).segment(cleaned)].map((s: any) => s.segment)
        : splitter.splitGraphemes(cleaned);

    if (clusters.length !== 1) return false; // more than one visible symbol

    // 2️⃣ check that the single cluster is an emoji (fully-qualified sequences included)
    try {
        const regex = emojiRegex();
        return regex.test(clusters[0]);
    } catch (e) {
        console.error("emoji validation error:", e);
        return false;
    }
};

const NextAOTD: React.FC<NextAOTDProps> = ({ nextEmojis, onEmojisSet }) => {
    const [emojis, setEmojis] = useState(["", "", ""]);
    const [inputError, setInputError] = useState([false, false, false]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [emojiSet, setEmojiSet] = useState(nextEmojis.length > 0);
    const [nnextEmojis, setnextEmojis] = useState(nextEmojis);
    const [errorDetails, setErrorDetails] = useState<string[]>([]);
    const [reusedIndexes, setReusedIndexes] = useState<number[]>([]);

    const handleEmojiChange = (index: number, value: string) => {
        // Allow empty inputs or valid single emojis
        if (value && !isEmojiCharacter(value)) {
            triggerInputError(index);
            return;
        }
        updateEmojis(index, value);
        focusNextInput(index, value);
    };

    const triggerInputError = (index: number) => {
        const newInputError = [...inputError];
        newInputError[index] = true;
        setInputError(newInputError);
        setTimeout(() => {
            newInputError[index] = false;
            setInputError(newInputError);
        }, 500);
    };

    const updateEmojis = (index: number, value: string) => {
        const newEmojis = [...emojis];
        newEmojis[index] = value;
        setEmojis(newEmojis);
    };

    const focusNextInput = (index: number, value: string) => {
        // wait for react to apply state update before moving focus
        if (value && index < emojis.length - 1) {
            setTimeout(() => {
                const nextInput = document.getElementById(`emoji-input-${index + 1}`);
                if (nextInput) {
                    (nextInput as HTMLInputElement).focus();
                }
            }, 0);
        }
    };

    const handleSubmit = async () => {
        // Filter out empty fields
        const filteredEmojis = emojis.filter(emoji => emoji.trim() !== "");

        // Require at least one emoji
        if (filteredEmojis.length === 0) {
            setErrorMessage("Please enter at least one emoji.");
            return;
        }

        if (!areAllEmojisValid(filteredEmojis)) {
            setErrorMessage("Please enter valid emoji characters only.");
            return;
        }

        await submitEmojis(filteredEmojis);
    };

    // check if at least one field has an emoji
    const hasAtLeastOneEmoji = () => {
        return emojis.some(emoji => emoji.trim() !== "");
    };

    const areAllEmojisValid = (emojis: string[]) => {
        return emojis.every((emoji) => isEmojiCharacter(emoji));
    };

    const submitEmojis = async (filteredEmojis: string[]) => {
        try {
            const headers = setHeaders();
            const response = await axios.post(
                url + "/api/art-of-the-day/set-next-aotd-emojis",
                { emojis: filteredEmojis },
                headers
            );

            if (response.status === 200) {
                toast.success("Emojis updated successfully.");
                setErrorMessage(null);
                setEmojiSet(true);
                setnextEmojis(filteredEmojis);
                onEmojisSet(filteredEmojis);
            }
        } catch (error: any) {
            handleSubmissionError(error);
        }
    };

    const handleSubmissionError = (error: any) => {
        if (error.response && error.response.status === 400 && error.response.data.reusedEmojis) {
            const reusedEmojiArr: { emoji: string; date: string }[] = error.response.data.reusedEmojis;

            // format list for display
            const formatted = reusedEmojiArr.map((e) => {
                const formattedDate = new Intl.DateTimeFormat("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric"
                }).format(new Date(e.date));
                return `${e.emoji} (used on ${formattedDate})`;
            });

            // set detailed error info for bullet rendering
            setErrorDetails(formatted);

            // find indexes of reused emojis to mark placeholders red
            const reusedIdx = emojis
                .map((em, idx) => (reusedEmojiArr.some((r) => r.emoji === em) ? idx : -1))
                .filter((i) => i !== -1);
            setReusedIndexes(reusedIdx);

            // keep existing emoji in the field; just highlight it with red border

            setErrorMessage("Some emojis have been used before. please choose different ones.");
            toast.error("Some emojis have been used in the past. Please choose different emojis.");
        } else if (error.response && error.response.data && error.response.data.message) {
            // show backend-provided error message to the user
            setErrorMessage(error.response.data.message);
            toast.error(error.response.data.message);
        } else {
            console.error("Error updating emojis:", error);
            toast.error("An error occurred while updating the emojis.");
        }
    };

    // Disable submit button if no emoji is entered
    const isSubmitDisabled = !hasAtLeastOneEmoji();

    return emojiSet ? (
        // <DisplayEmojis nnextEmojis={nnextEmojis} />
        null
    ) : (
        <EmojiInputForm
            emojis={emojis}
            inputError={inputError}
            handleEmojiChange={handleEmojiChange}
            handleSubmit={handleSubmit}
            isSubmitDisabled={isSubmitDisabled}
            errorMessage={errorMessage}
            errorDetails={errorDetails}
            reusedIndexes={reusedIndexes}
        />
    );
};

const DisplayEmojis: React.FC<{ nnextEmojis: string[] }> = ({ nnextEmojis }) => (
    <div className="flex flex-row items-center max-w-md mx-auto">
        <p className="text-gray-800 text-base m-4">
            <span className="font-bold ">next AOTD emojis: </span>
        </p>
        <div className="flex gap-2">
            {nnextEmojis.map((emoji, index) => emoji && (
                <span key={index} className="text-2xl">
                    {emoji}
                </span>
            ))}
        </div>
    </div>
);

const EmojiInputForm: React.FC<{
    emojis: string[];
    inputError: boolean[];
    handleEmojiChange: (index: number, value: string) => void;
    handleSubmit: () => void;
    isSubmitDisabled: boolean;
    errorMessage: string | null;
    errorDetails: string[];
    reusedIndexes: number[];
}> = ({ emojis, inputError, handleEmojiChange, handleSubmit, isSubmitDisabled, errorMessage, errorDetails, reusedIndexes }) => (
    <div className="flex flex-col m-2 border-4 border-gray-500 shadow-indigo-400 rounded-sm shadow items-center p-5 max-w-md mx-auto">
        <p className="text-gray-800 text-base m-2">
            <span className="font-bold text-indigo-500">your next AOTD's emojis </span>
        </p>
        <small className="text-gray-400 mb-2 text-justify">
            these emojis will be used to create the next AOTD. please enter at least one emoji. mind that you cannot reuse the same emoji ever again.
        </small>
        <div className="flex gap-2">
            {emojis.map((emoji, index) => (
                <input
                    id={`emoji-input-${index}`}
                    key={index}
                    type="text"
                    value={emoji}
                    onChange={(e) => handleEmojiChange(index, e.target.value)}
                    placeholder="-"
                    className={`w-12 p-2 rounded border-2 text-center text-xl focus:outline-none 
            ${inputError[index]
                            ? "border-red-500 focus:border-red-600 border-spacing-4 animate-wiggle"
                            : "border-gray-300 focus:border-blue-500"
                        } ${reusedIndexes.includes(index) ? "border-red-500 focus:border-red-600 placeholder-red-500" : "placeholder-gray-400"}`}
                />
            ))}
        </div>
        <BasicButton
            text="Set Emojis"
            onClick={handleSubmit}
            bgColor="bg-blue-500"
            hoverBgColor="hover:bg-blue-600"
            textColor="text-white"
            className="mt-4"
            disabled={isSubmitDisabled}
        />
        {/* enhanced error message */}
        {errorMessage && (
            <div className="flex flex-col items-start w-full mt-3 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
                <div className="flex items-start">
                    <FaExclamationCircle className="flex-shrink-0 h-5 w-5 text-red-600 mr-2" />
                    <span>{errorMessage}</span>
                </div>
                {errorDetails.length > 0 && (
                    <ul className="list-disc list-inside mt-2">
                        {errorDetails.map((detail, idx) => (
                            <li key={idx}>{detail}</li>
                        ))}
                    </ul>
                )}
            </div>
        )}
    </div>
);

export default NextAOTD;

